import React, { useMemo, useState } from 'react';
import { message } from 'antd';
import { ApprovalWorkflowItemDto, AWAssetDto, AWStageDto } from '@api/Api';
import {
  useRestartApprovalWorkflow,
  useSendReminderMemberApprovalStage,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import ApprovalPreviewCollapse from '@components/Modals/ApprovalModal/components/ApprovalPreview/components/ApprovalPreviewCollapse';
import { nonNull } from '@helpers/non-null';
import type { RestartScope } from '@components/Modals/ApprovalModal/RestartApprovalModal/RestartApprovalModal';
import { SuccessIcon } from '@icons';

const RestartApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/RestartApprovalModal')
);

type RestartConfirmationValue = {
  visible: boolean;
  stage?: AWStageDto & { stageNumber: number };
};

type AssetModalStagesProps = {
  asset: AWAssetDto;
  approvalWorkflow: ApprovalWorkflowItemDto;
  sendMixpanelEvent: () => void;
  onUpdateApprovalWorkflow: (approvalWorkflow: ApprovalWorkflowItemDto) => void;
};

export default function AssetModalStages({
  approvalWorkflow,
  asset,
  sendMixpanelEvent,
  onUpdateApprovalWorkflow
}: AssetModalStagesProps) {
  const canManage = approvalWorkflow.permissions.manage;
  const { currentStageId } = approvalWorkflow;

  const [restartConfirmation, setRestartConfirmation] =
    useState<RestartConfirmationValue>({ visible: false });

  const stages = useMemo(
    () => approvalWorkflow.stages || [],
    [approvalWorkflow.stages]
  );

  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({});

  const {
    mutateAsync: restartApprovalWorkflow,
    isPending: isLoadingRestartApprovalWorkflow
  } = useRestartApprovalWorkflow({});

  const { mutateAsync: sendReminderMemberApprovalStage } =
    useSendReminderMemberApprovalStage({});

  const onSendReminder = async (stageId: string, email: string) => {
    if (!approvalWorkflow.id) return;
    sendMixpanelEvent?.();
    message.success({
      content: 'Reminder has been sent!',
      className: 'message-dark-modal',
      icon: <SuccessIcon className="anticon" />
    });
    await sendReminderMemberApprovalStage({
      id: approvalWorkflow.id,
      stageId,
      emails: [email]
    });
  };

  const toggleSkipMemberDecision = async (
    stageId: string,
    email: string,
    decisionSkipped: boolean
  ) => {
    if (isLoadingUpdateApprovalWorkflow) return;
    if (!approvalWorkflow.id) return;
    if (!asset) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await updateApprovalWorkflow({
        id: approvalWorkflow.id,
        stages: {
          update: [
            {
              id: stageId,
              members: {
                update: [
                  {
                    email,
                    decisions: [
                      { decisionSkipped, assetVersionId: asset.versionId }
                    ]
                  }
                ]
              }
            }
          ]
        }
      });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const resetMemberDecision = async (stageId: string, email: string) => {
    if (isLoadingUpdateApprovalWorkflow) return;
    if (!approvalWorkflow.id) return;
    if (!asset?.versionId) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await updateApprovalWorkflow({
        id: approvalWorkflow.id,
        stages: {
          update: [
            {
              id: stageId,
              members: {
                update: [
                  {
                    email,
                    decisions: [
                      {
                        assetVersionId: asset.versionId,
                        decision: null
                      }
                    ]
                  }
                ]
              }
            }
          ]
        }
      });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onRestart = async (stageId: string, scope: RestartScope) => {
    if (isLoadingRestartApprovalWorkflow) return;
    if (!approvalWorkflow.id) return;
    if (!stageId) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await restartApprovalWorkflow({
        id: approvalWorkflow.id,
        stageId,
        assetId: scope === 'asset' ? asset.id : undefined
      });
      setRestartConfirmation({ ...restartConfirmation, visible: false });
      sendMixpanelEvent?.();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
      const stage = nonNull(
        approvalWorkflow.stages.find((x) => x.id === stageId)
      );
      message.success({
        key: 'aw-restarted',
        content:
          scope === 'asset'
            ? `${asset.name}.${asset.extension} approval routing has been restarted from stage ${stage.name}.`
            : `${approvalWorkflow.name} approval routing has been restarted for all assets from stage ${stage.name}.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const formattedStages = useMemo(() => {
    const _stages = stages.map((stage, i) => {
      const stageStatus = nonNull(
        asset.statuses?.find((x) => x.stageId === stage.id)
      );
      return {
        ...stage,
        status: stageStatus.status,
        isFinished: stageStatus.isFinished,
        stageNumber: i + 1
      };
    });
    const completedStages = _stages.filter((stage) => stage.isFinished) || [];
    const inProgressStage =
      _stages
        .filter((stage) => !completedStages.includes(stage))
        .filter((stage) => stage.id === currentStageId) || [];
    const pendingStages = _stages
      .filter((stage) => !completedStages.includes(stage))
      .filter((stage) => !inProgressStage.includes(stage));

    return [
      {
        key: 'completed',
        stages: completedStages,
        copy: 'COMPLETED STAGES',
        canSkipDecision: false
      },
      {
        key: 'in-progress',
        stages: inProgressStage,
        copy: 'IN PROGRESS STAGE',
        canSkipDecision: canManage
      },
      {
        key: 'pending',
        stages: pendingStages,
        copy: 'PENDING STAGES',
        canSkipDecision: canManage
      }
    ];
  }, [asset, stages, currentStageId, canManage]);

  const defaultOpenedStage = useMemo(() => {
    const completed =
      formattedStages.find((x) => x.key === 'completed')?.stages || [];
    const inProgress =
      formattedStages.find((x) => x.key === 'in-progress')?.stages || [];
    const pending =
      formattedStages.find((x) => x.key === 'pending')?.stages || [];
    const openedStage =
      inProgress[0] || pending[0] || completed[completed.length - 1];
    return openedStage?.id;
  }, [formattedStages]);

  return (
    <>
      {formattedStages
        .filter((x) => x.stages.length)
        .map(({ stages, canSkipDecision, copy, key }) => {
          return (
            <ApprovalPreviewCollapse
              key={key}
              defaultOpenedStage={defaultOpenedStage}
              assetVersionId={asset?.versionId || ''}
              currentStageId={approvalWorkflow.currentStageId}
              isShowDecision={true}
              stages={stages}
              copy={copy}
              toggleSkipMemberDecision={
                canSkipDecision ? toggleSkipMemberDecision : undefined
              }
              resetMemberDecision={canManage ? resetMemberDecision : undefined}
              onSendReminder={
                canManage && !approvalWorkflow.isPaused
                  ? onSendReminder
                  : undefined
              }
              onRestart={(stage) => {
                setRestartConfirmation({ stage, visible: true });
              }}
              approvalWorkflow={approvalWorkflow}
            />
          );
        })}
      <RestartApprovalModal
        visible={restartConfirmation.visible}
        stage={restartConfirmation.stage}
        awAsset={asset}
        onCancel={() =>
          setRestartConfirmation({ ...restartConfirmation, visible: false })
        }
        onConfirm={(scope) => {
          if (!restartConfirmation.stage) return;
          onRestart(restartConfirmation.stage.id, scope);
        }}
      />
    </>
  );
}
